<script lang="ts">
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';

  import type { AssetStoryblok, SalesHeroStoryblok } from '$types/bloks';
  import Image from '$components/image.svelte';
  import { buildSizes, buildSrcSet } from '$lib/image';
  import PromotionalBlock from '$components/promotional-block.svelte';
  import { onMount } from 'svelte';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media';
  import { cn } from '$lib/utils';

  export let block: SalesHeroStoryblok;
  let video: HTMLVideoElement;
  const desktop = createBreakpointMediaQueryStore('lg');
  let salesVideo: AssetStoryblok | undefined = undefined;
  let videoImage: AssetStoryblok | undefined = undefined;
  let poster: string | undefined = undefined;

  onMount(() => {
    document.body.setAttribute('data-home-sales-color', block.color || '1');
    const unsubscribe = desktop.subscribe((value) => {
      salesVideo = value ? block.video : block.mobile_video;
      videoImage = !value && block.mobile_image ? block.mobile_image : block.image;
      poster = videoImage?.filename
        ? getImageAttributes(videoImage, { size: [1440, 0] })?.src
        : undefined;

      // clean up the subscription on unmount
      return () => unsubscribe();
    });
  });
</script>

{#if block}
  {@const link = block.link ? $getAnchorFromCmsLink(block.link) : null}
  <section class="flex overflow-hidden p-2 lg:p-8" use:storyblokEditable={block}>
    <div class="container relative mx-auto flex flex-wrap flex-col lg:flex-row gap-y-2 gap-x-4">
      <div class="grow">
        {#if block.image?.filename}
          <a
            href={link?.href}
            rel={link?.rel}
            target={link?.target}
            class="relative overflow-hidden rounded-md z-10 flex w-full h-[520px] grow p-4"
          >
            {#if salesVideo?.filename}
              <div class="overflow-hidden absolute inset-0 w-full h-full">
                <video
                  {poster}
                  bind:this={video}
                  autoplay
                  loop
                  muted
                  playsinline
                  src={salesVideo?.filename}
                  class="min-w-full min-h-full w-auto h-auto absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-cover object-center rounded-md block"
                />
              </div>
            {:else}
              <picture>
                {#if block.mobile_image?.filename}
                  <source
                    media="(max-width: 768px)"
                    sizes="100vw"
                    srcset={buildSrcSet({
                      loader: ({ width }) => {
                        return block.mobile_image
                          ? getImageAttributes(block.mobile_image, { size: [width, 0] }).src
                          : '';
                      },
                      widths: [(w) => w]
                    })}
                  />
                {/if}
                <source
                  sizes={buildSizes({
                    default: '100vw',
                    lg: '1440px'
                  })}
                  srcset={buildSrcSet({
                    loader: ({ width }) => {
                      return block.image
                        ? getImageAttributes(block.image, { size: [width, 0] }).src
                        : '';
                    },
                    widths: [1440, (w) => (w < 1440 ? w : null)]
                  })}
                />
                <Image
                  loading="eager"
                  class="absolute inset-0 -z-10 h-full w-full scale-[var(--scale)] transform-gpu rounded-md object-cover overflow-hidden"
                  {...getImageAttributes(block.image, { size: [1440, 0] })}
                />
              </picture>
            {/if}

            {#if block.title}
              <p
                class="text-3xl z-[11] text-center pointer-events-none font-semibold text-highlight w-full p-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              >
                {block.title}
              </p>
            {/if}

            <div
              class="flex gap-2 absolute w-full left-0 bottom-0 lg:w-auto lg:left-4 lg:bottom-4 px-2.5 py-3.5 lg:px-0 lg:py-0"
            >
              {#if block.cta_label}
                <p class=" flex-1 lg:flex-none">
                  <span
                    class="w-full inline-block text-highlight text-base bg-primary/18 px-4.5 py-3 lg:py-3.5 rounded-xs text-center font-medium lg:font-semibold backdrop-blur"
                    >{block.cta_label}</span
                  >
                </p>
              {/if}

              {#if block.second_cta_label && block.second_cta_link}
                <a
                  class=" flex-1 lg:flex-none"
                  href={$getAnchorFromCmsLink(block.second_cta_link).href}
                >
                  <span
                    class="w-full inline-block text-base bg-background px-4.5 py-3 lg:py-3.5 rounded-xs text-center font-medium lg:font-semibold backdrop-blur"
                    >{block.second_cta_label}</span
                  >
                </a>
              {/if}
            </div>
          </a>
        {/if}
      </div>

      {#if block.promo_blocks && !!block.promo_blocks.length}
        {@const existsHightlight = block.promo_blocks.find((b) => b.show_as_highlight == true)}
        <div class="flex lg:flex-col w-full lg:w-1/4 min-h-[85px] flex-wrap gap-y-4 gap-x-2">
          {#each block.promo_blocks as promo_block, i}
            <PromotionalBlock
              block={promo_block}
              variant="sm"
              color={block.campaign_color}
              isFirst={i == 0}
              isLast={i == block.promo_blocks.length - 1}
              class={cn(
                'p-2.5 pb-2 lg:p-3 flex-1',
                existsHightlight &&
                  (promo_block.show_as_highlight && i == 0 ? 'min-w-full' : 'lg:flex-none')
              )}
            />
          {/each}
        </div>
      {/if}
    </div>
  </section>
{/if}
